import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/ducks';
import store from 'store';
import { format } from 'date-fns';

import { ICategoriesReasons, IPipefyOption } from 'components/Select/interface';
import { useCustomEvent } from 'components/ApplicationInsights';
import SwitchComponent from 'components/SwitchComponent';
import { Row, Column } from 'components/Layout/Grid';
import FloatingLabel from 'components/FloatingLabel';
import { SimpleForm } from 'components/SimpleForm';
import { Visibility } from 'components/Visibility';
import RadioInput from 'components/RadioInput';
import toast from 'components/Toast/dispatch';
import Validator from 'components/Validator';
import { Button } from 'components/Button';
import Text from 'components/Text';
import Form from 'components/Form';
import {
  abrirProtocoloAction,
  eventName,
  protocolosAction
} from 'components/ApplicationInsights/eventNames';

import { NfDevolution } from './NfDevolution';
import RemainingBalance from './RemainingBalance';
import { LotSelectedProps } from './LotProduct/interface';
import { DiscountValue } from './DiscountValue';
import OccurrenceType from './OcurrenceType';
import SampleType from './SampleType';
import ProductSampleType from './ProductSampleType';
import TechnicalDescription from './TechnicalDescription';
import OccurrenceDate from './OccurrenceDate';
import CustomerData from './CustomerData';
import ProtocolCategory from './ProtocolCategory';
import ContactData from './ContactData';
import Description from './Description';
import LinkType from './LinkType';
import LinkProtocol from './LinkProtocol';
import DropAddress from './DropAddress';
import LotProduct from './LotProduct';
import RIProduct from './RIProduct';
import Brand from './Brand';
import BU from './BU';

import attachmentIcon from 'img/icon-anexo.svg';
import IconSuccess from 'img/success-img.svg';

import { EnumToArray } from 'utils/enumArray/enumHelpers';
import { PhoneNumberParse } from 'utils/PhoneNumber';

import UploadService from 'services/upload.service';
import TicketServices from 'services/ticket.service';
import DomainSettingService from 'services/domainSetting.service';
import PipefyService from 'services/pipefy.service';
import StorageFileService from 'services/storageFile.service';

import { IDomainSetting } from 'models/DomainSetting';
import { FileOriginType } from 'models/StorageFile';
import { IOrderAttachments } from 'models/Order';
import { UserProfileTypes } from 'models/User';
import { IAddress } from 'models/Address';
import {
  EnumCategories,
  EnumNameProtocolStatus,
  EnumProfileType,
  EnumReason,
  ILegalPersonType,
  ITicketAttendanceType
} from 'models/Ticket';

import {
  FileTypesAccept,
  ILinkProtocol,
  ITypesRequired,
  phaseToEditProtocol,
  phaseToEditProtocolMissingVolume
} from './interface';

import { EqualTo, In } from 'utils/Patterns/Specification';
import { convertStringToFloat } from 'utils/ConvertStringToFloat';
import { SetMessageErrorNewProtocol } from 'utils/OpenProtocol';

import { CustomerDataContextProvider } from 'contexts/OpenProtocol/CustomerData/CustomerDataContext';
import { OpenProtocolContextProvider, useOpenProtocolContext } from 'contexts/OpenProtocol';
import { ObjectIsEmpty } from 'utils/Object';

const OpenProtocolMain = () => {

  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.user);
  const products = useSelector((state: RootState) => state.openProtocolData.products);
  const order = useSelector((state: RootState) => state.openProtocolData?.order);

  const cte = useSelector((state: RootState) => state.openProtocolData.cte);
  const cteInvoice = useSelector((state: RootState) => state.openProtocolData?.cteInvoice);

  const { disableCte } = useOpenProtocolContext();

  const isEditing = !!location?.state?.protocolNumber;
  const company = location?.state?.company || store.getState().customer;
  const isAutoAttendence = location?.state?.communicationChannel === 'AUTO_ATTENDANCE';

  const [fail, setFail] = useState(false);

  const [brands, setBrands] = useState(location?.state?.brands || '');

  const [buSelected, setBuSelected] = useState(location?.state?.businessUnits || '');
  const [returnType, setReturnType] = useState(location?.state?.returnType || '');
  const [dropAddressSameInvoice, setDropAddressSameInvoice] = useState(
    location?.state?.dropAddressSameInvoice === undefined
      ? true
      : location?.state?.dropAddressSameInvoice
  );
  const [dropAddress, setDropAddress] = useState<IAddress>(location?.state?.dropAddress || {});
  const [linkType, setLinkType] = useState('');
  const [linkProtocol, setLinkProtocol] = useState<ILinkProtocol>({ protocolNumber: location?.state?.linkProtocolNumber });

  const [selectedLinkProtocol, setSelectedLinkProtocol] = useState(false);
  const [customerId, setCustomerId] = useState(location?.state?.customerId || user?.customerId);
  const [protocolNumber] = useState(location?.state?.protocolNumber || '');
  const [legalPerson, setLegalPerson] = useState(
    ILegalPersonType[location?.state?.userTypeProtocol] ||
    ILegalPersonType.JURIDICAL_PERSON
  );
  const [communicationChannel, setCommunicationChannel] = useState(location?.state?.communicationChannel || '');
  const [ticketReason, setTicketReason] = useState(location?.state?.ticketReason || '');
  const [protocolCategory, setProtocolCategory] = useState(location?.state?.protocolCategory || '');
  const [protocolClassification, setProtocolClassification] = useState(location?.state?.protocolClassification || '');
  const [protocolNumberCreated, setProtocolNumberCreated] = useState('');

  const [tags, setTags] = useState<any>(location?.state?.tags || []);

  const [files, setFiles] = useState<Array<any>>(
    location?.state?.attachments || []
  );

  const [filesRequiredReturn, setFilesRequiredReturn] = useState('');
  const [, setFilesRequiredReclamation] = useState('');

  const [technicalComplaintFile,] = useState<any>(
    location?.state?.technicalComplaintAttachment || undefined
  );

  const [tagInput, setTagInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [disableCustomer] = useState(isEditing || store.getState().customer?.id);
  const [disableProduct, setDisableProduct] = useState(false);
  const [disableOrder, setDisableOrder] = useState(false);

  const [optionalButtonsTypes, setOptionalButtonsTypes] = useState(Array<IDomainSetting>());
  const [lot, setLot] = useState<LotSelectedProps>({
    number: location?.state?.lot,
    lotExpirationDate: location?.state?.lotExpirationDate
      ? format(new Date(location.state.lotExpirationDate), 'yyyy-MM-dd')
      : '',
    lotManufacturingDate: location?.state?.lotManufacturingDate
      ? format(new Date(location.state.lotManufacturingDate), 'yyyy-MM-dd')
      : ''
  });

  const [manualDevolutionInvoiceNumber, setManualDevolutionInvoiceNumber] = useState(
    location?.state?.manualDevolutionInvoiceNumber || ''
  );
  const [productRI, setProductRI] = useState(location?.state?.productRI || '');
  const [allCategories, setAllCategories] = useState<Array<IPipefyOption>>([]);
  const [allReasons, setAllReasons] = useState<Array<IPipefyOption>>([]);
  const [allClassifications, setAllClassifications] = useState<Array<IPipefyOption>>([]);

  const [othersOccurrence, setOthersOccurrence] = useState(location?.state?.occurrenceTypeDescription || '');
  const [occurrenceType, setOccurrenceType] = useState(location?.state?.occurrenceType || '');
  const [sampleType, setSampleType] = useState<string>(location?.state?.sampleType || '');
  const [technicalDescription, setTechnicalDescription] = useState(location?.state?.description || '');
  const [office, setOffice] = useState();
  const [occurrenceDate, setOccurrenceDate] = useState(
    location?.state?.occurrenceDate ?
      format(new Date(location?.state?.occurrenceDate), 'yyyy-MM-dd')
      : ''
  );
  const [uncontaminatedProductSampleType, setUncontaminatedProductSampleType] = useState<string>(
    isEditing && location?.state?.availableSampleCollection.toString() || '');
  const [remainingBalance, setRemainingBalance] = useState<boolean | undefined>(
    location?.state?.balanceRemaining || ''
  );
  const [qtdRemainingBalance, setQtdRemainingBalance] = useState<string | undefined>(
    location?.state?.balanceRemainingQuantity || ''
  );
  const [enterprisesManualProtocol, setEnterprisesManualProtocol] = useState<string[]>([]);
  const [discountValue, setDiscountValue] = useState<string>('');
  const [selectedResponsibleGroup, setSelectedResponsibleGroup] = useState<string>('');
  const [filesMap, setFilesMap] = useState<React.ReactNode[]>([]);

  const trackMenuClick = useCustomEvent('Button', eventName.PROTOCOLOS);

  useEffect(() => {
    if (user && user?.userType === 'customer' && Array.isArray(user.selectedCustomerId) && !isEditing)
      navigate('/dashboard');
  }, [user?.selectedCustomerId]);

  useEffect(() => {
    if (isEditing) {
      trackMenuClick({ item: protocolosAction.EDITARPROTOCOLO });

      if (location?.state?.discountValue)
        setDiscountValue(String(location?.state.discountValue));
    }
  }, []);

  useEffect(() => {
    setTicketReason('');
  }, [legalPerson]);

  const _uploadService = new UploadService();
  const _ticketService = new TicketServices();
  const _domainSettingService = new DomainSettingService();

  function dispatchLinkTypeData(type: string, value: any) {
    dispatch({ type: type, data: value });
  }

  function resetOrderAndProducts() {
    dispatch({ type: 'RESET_OPEN_PROTOCOL_DATA' });
  }

  function addNewTag(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!tagInput) return;

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();

      setTags((prevTags: any) => [...prevTags, tagInput.trim()]);
      setTagInput('');
    }
  }

  function removeTag(id: number) {
    const _tags: Array<string> = tags;

    _tags.splice(id, 1);

    return setTags([...tags]);
  }

  function addFile(e: any) {
    const fileTypes: Array<any> = FileTypesAccept;
    const arrayFiles: Array<any> = [...e.target.files];

    arrayFiles.forEach((file) => {
      fileTypes.includes(file.type) && setFiles((olfFiles) => [...olfFiles, file]);
    });

    e.target.value = "";
  }

  function removeFile(id: number) {
    const _files: Array<any> = files;

    _files.splice(id, 1);

    return setFiles([..._files]);
  }

  async function getCategoriesReasons() {
    const options: ICategoriesReasons = await _ticketService.getCategoriesReasons();

    const { data } = options.data;
    if (data) {

      setAllCategories(data.categories);
      setAllReasons(data.reasons);
      setAllClassifications(data.classifications);

      if (isEditing) {
        if (location?.state?.protocolCategory) {
          setProtocolCategory(data.categories.find(
            x => x.pipefyLabel === location.state.protocolCategory)?.value
          );
        }
        if (location?.state?.ticketReason) {
          setTicketReason(data.reasons.find(
            x => x.pipefyLabel === location.state.ticketReason)?.value
          );
        }
        if (location?.state?.protocolClassification) {
          setProtocolClassification(data.classifications.find(
            x => x.pipefyLabel === location.state.protocolClassification)?.value
          );
        }
      }
    }
  }

  useEffect(() => {
    getCategoriesReasons();
    getPipefyGroup();
  }, []);

  const getPipefyGroup = async () => {
    try {
      const cardId = location?.state?.cardId;
      if (!cardId) {
        return;
      }
      const result = await new PipefyService().findCard(cardId);
      if (result) {
        const responsibleGroupForm = getResponsibleGroup(result);
        setSelectedResponsibleGroup(responsibleGroupForm);
      }
    } catch (error) {
      console.error('Erro ao buscar o cartão:', error);
    }
  };

  function getResponsibleGroup(result: any) {
    const { card } = result.data;

    const fieldCurrentPhase = card.current_phase.fields.find(
      (item: any) => item.label.toUpperCase() === "GRUPO RESPONSÁVEL"
    );

    if (!fieldCurrentPhase) return "";

    if (fieldCurrentPhase.options.length <= 1) {
      return fieldCurrentPhase.options[0];
    }

    const field = card.fields.find(
      (item: any) => item.field.id === fieldCurrentPhase.id
    );

    return field?.value || "";
  }


  function onCheckSubmit(data: any) {
    if (legalPerson !== ILegalPersonType.MANUAL_CUSTOMER_CONSUMER) {
      setLoading(true);
      setErrorMessage('');

      if (isEditing) {
        data = { ...location?.state, ...data };

        const status = data?.phaseStatus?.toLowerCase();
        const allowEdit = phaseAllowedForEditing(status);

        if (allowEdit) {
          onSubmit(data);
          return;
        }

        if (!allowEdit) {
          setErrorMessage(`Desculpe, mas você não possui permissão para editar protocolos na etapa de (${status}). Caso precise de acesso, por favor, abra um chamado no TopDesk.`);
          setLoading(false);
          return;
        }
      }

      onSubmit(data);
    }

    return false;
  }

  const phaseAllowedForEditing = (status: string) => {
    const isMissingVolumePhase = phaseToEditProtocolMissingVolume.includes(status);

    if (isMissingVolumePhase) {
      return isAuthorizedGroupAndReason();
    }

    return isOkToEditPhase(status);
  };

  const isAuthorizedGroupAndReason = () => {

    const profileTypesValues = user?.profileType?.flatMap(key => {
      const value = getEnumValueByKey(key);
      return value ? [value.toUpperCase()] : [];
    }) || [];

    const isSelectedGroupPresent = profileTypesValues.includes(selectedResponsibleGroup);

    return ticketReason === EnumReason.FALTA_DE_VOLUME && isSelectedGroupPresent;
  };

  function getEnumValueByKey(key: keyof typeof UserProfileTypes): string | undefined {
    return UserProfileTypes[key];
  }

  const isOkToEditPhase = (status: string) => {
    if (!phaseToEditProtocol.includes(status)) return false;

    return isCommercialEvaluationAllowed(status) && isLogisticEvaluationAllowed(status);
  };

  const isCommercialEvaluationAllowed = (status: string) => {
    return !(status === EnumNameProtocolStatus.AVALIACAO_COMERCIAL &&
      !user?.profileType?.includes(EnumProfileType.INTERNAL_COMMERCIAL));
  };

  const isLogisticEvaluationAllowed = (status: string) => {
    return !(status === EnumNameProtocolStatus.AVALIACAO_LOGISTICA &&
      protocolCategory !== EnumCategories.OCORRENCIA_TRANSPORTADORA);
  };

  async function onSubmit(data: any) {

    if (data.customerId === undefined) {
      data.customerId =
        store.getState().customer?.id ||
        user?.selectedCustomerId ||
        user?.customerId;
    }

    if (user?.userType === 'customer') {
      data.communicationChannel = data.communicationChannel || "PORTAL_CC";
    } else data.communicationChannel = communicationChannel || undefined;

    data.userTypeProtocol = legalPerson;
    data.legalPerson = data.documentNumber?.replace(/[.\-\/\s()]/g, '').length === 11 ? ILegalPersonType.NATURAL_PERSON : ILegalPersonType.JURIDICAL_PERSON || undefined;
    data.documentNumber =
      data.documentNumber?.replace(/[.\-\/\s()]/g, '') || undefined;
    data.protocolNumber = protocolNumber || undefined;
    data.order = !disableOrder ? order : undefined;
    data.products = !disableProduct ? products : undefined;
    data.electronicBillLadingId = !disableCte ? cte?.[0]?.id : undefined;
    data.invoiceId = !ObjectIsEmpty(cteInvoice) ? cteInvoice?.[0]?.id : undefined;
    data.contactPhoneNumber = data.contactPhoneNumber
      ? PhoneNumberParse(data.contactPhoneNumber)
      : undefined;

    data.protocolCategory = data.protocolCategory
      ? allCategories.find(x => x.value === data.protocolCategory)?.pipefyLabel
      : undefined;
    data.ticketReason = data.ticketReason
      ? allReasons.find(x => x.value === data.ticketReason)?.pipefyLabel
      : undefined;
    data.protocolClassification = data.protocolClassification
      ? allClassifications.find(x => x.value === data.protocolClassification)?.pipefyLabel
      : undefined;

    data.tags = tags;
    data.brands = brands || undefined;
    data.businessUnits = buSelected || '';

    data.discountValue = convertStringToFloat(discountValue);

    data.attendenceType = EnumToArray(ITicketAttendanceType).find(
      (x) => x.value === ITicketAttendanceType.SERVED_BY_ATTENDENT
    )?.key;

    const indexReclamacao = allCategories.find(x => x.label === ITypesRequired.RECLAMACAO_TECNICA)?.value;
    const indexDevolucao = allCategories.find(x => x.label === ITypesRequired.DEVOLUCAO)?.value;

    if (legalPerson === ILegalPersonType.JURIDICAL_PERSON) {
      if (
        data.protocolCategory === indexReclamacao
      ) {
        if (
          data.order === undefined ||
          data.order.length === 0 ||
          data.order.items === undefined ||
          data.order.items.length === 0
        ) {
          setErrorMessage(
            'Categoria exige um pedido com pelo menos um item vinculado.'
          );
          setLoading(false);
          return;
        }
      }
    } else if (
      (data.protocolCategory === indexReclamacao ||
        data.protocolCategory === indexDevolucao) &&
      data.products.length === 0
    ) {
      setErrorMessage('Motivo exige um produto vinculado.');
      setLoading(false);
      return;
    }

    if (data.ticketReason === 35 && data.products.length === 0) {
      setErrorMessage('Motivo exige um produto vinculado.');
      setLoading(false);
      return;
    }

    data.dropAddressSameInvoice = dropAddressSameInvoice;
    data.dropAddress = dropAddressSameInvoice === true ? undefined : dropAddress;
    if (returnType !== '') data.returnType = returnType;

    if (data.dropAddress?.zipCode) {
      data.dropAddress.zipCode =
        data.dropAddress?.zipCode?.replace(/\D/g, '') || undefined;
    }

    if (technicalComplaintFile) {
      const technicalComplaintFileUploaded =
        technicalComplaintFile.uri !== undefined
          ? [technicalComplaintFile]
          : [_uploadService.upload(technicalComplaintFile)];

      data.technicalComplaintAttachment = await Promise.all(
        technicalComplaintFileUploaded
      )
        .then((filesTechnicalComplaintAttachment: any) =>
          filesTechnicalComplaintAttachment.map((file: any) => ({
            id: file.id,
            name: file.name,
            fileUrl: file.uri || file.fileUrl,
          }))
        )
        .catch(() => {
          setLoading(false);
          toast.error('Falha ao anexar arquivo(s).');
          return false;
        });

      // eslint-disable-next-line prefer-destructuring
      data.technicalComplaintAttachment = data.technicalComplaintAttachment[0];
    }

    const invalidFiles = files?.find(f => f.size == 0);
    if (invalidFiles) {
      setErrorMessage('Existe(m) arquivo(s) anexado(s) com o tamanho inválido.');
      setLoading(false);
      return;
    }

    const uploadedFiles = files.map((file) =>
      file.uri !== undefined ? file : _uploadService.upload(file)
    );

    data.attachments = await Promise.all(uploadedFiles)
      .then((filesAttachments: any) =>
        filesAttachments.map((file: any) => ({
          id: file.id,
          name: file.name,
          fileUrl: file.uri || file.fileUrl,
        }))
      )
      .catch(() => {
        setLoading(false);
        toast.error('Falha ao anexar arquivo(s).');
        return false;
      });

    if (user?.userType !== 'customer') {
      if (selectedLinkProtocol) {
        data.linkProtocolNumber = linkProtocol.protocolNumber;
      }
    }

    if (protocolCategory === EnumCategories.RECLAMACAO_TECNICA &&
      legalPerson === ILegalPersonType.NATURAL_PERSON) {
      data.lot = lot.number;
      data.lotManufacturingDate = lot.lotManufacturingDate;
      data.lotExpirationDate = lot.lotExpirationDate;
    }

    if (protocolCategory === EnumCategories.RECLAMACAO_TECNICA &&
      brands === '6179b025389e8d5c5f72fbb7') {
      data.productRI = productRI !== '' ? productRI : undefined;
    }

    if (protocolCategory === EnumCategories.RECLAMACAO_TECNICA) {
      data.occurrenceDate = occurrenceDate;
      data.businessPosition = office;
      data.balanceRemaining = remainingBalance;
      data.balanceRemainingQuantity = qtdRemainingBalance;
      data.occurrenceType = occurrenceType;
      data.occurrenceTypeDescription = othersOccurrence;
      data.sampleType = sampleType ? sampleType : '';
      data.availableSampleCollection = uncontaminatedProductSampleType ? JSON.parse(uncontaminatedProductSampleType) : false;
      data.technicalDescription = technicalDescription;
    }

    if (isEditing) {
      const response = await _ticketService.update(data);
      if (response?.status === 200) {
        setSuccess(true);
        ResetCompany();
      } else {
        if (response.data.code) {
          setErrorMessage(response.data.data);
        } else SetMessageErrorNewProtocol(setErrorMessage, response.data.data);
        setLoading(false);
      }
    } else {
      const response = await _ticketService.create(data);

      if (response?.status === 200) {
        setSuccess(true);
        setProtocolNumberCreated(response.data.data.protocolNumber);
        ResetCompany();
        trackMenuClick({ item: abrirProtocoloAction.REGISTRARPROTOCOLO });
      } else {
        if (response.data.code) {
          setErrorMessage(response.data.data);
        } else SetMessageErrorNewProtocol(setErrorMessage, response.data.data);
        setLoading(false);
      }
    }
  }

  function ResetCompany() {
    dispatch({ type: 'SET_OPEN_PROTOCOL_CUSTOMER', data: undefined });
  }

  async function getEnterprisesManualProtocol() {
    const response = await _domainSettingService
      .get(undefined, undefined, undefined, {
        and: [
          { identifier: { eq: 'EnterpriseManualProtocol' } },
          { enabled: { eq: true } },
        ],
      });

    const { data } = response;
    const enterprises = data?.[0]?.value.split(',');

    setEnterprisesManualProtocol(enterprises);
  }

  useEffect(() => {
    setLinkType('');
    resetOrderAndProducts();
    if (!isEditing) {
      setSelectedLinkProtocol(false);
    }
  }, [legalPerson, customerId]);

  useEffect(() => {
    if (isEditing && ticketReason === "55")
      setTicketReason('');

    if (!isEditing) setTicketReason('');
  }, [protocolCategory]);

  useEffect(() => {
    if (!isEditing) setProtocolClassification('');
  }, [ticketReason]);

  useEffect(() => {
    if (location?.state?.order) {
      dispatchLinkTypeData("SET_OPEN_PROTOCOL_ORDER", location?.state?.order);
      setLinkType('ORDER');
    }

    if (location?.state?.products && location?.state?.products.length) {
      dispatchLinkTypeData("SET_OPEN_PROTOCOL_PRODUCTS", location?.state?.products);
      setLinkType('PRODUCT');
    }

    if (location?.state?.electronicBillLading) {
      dispatchLinkTypeData("SET_OPEN_PROTOCOL_CTE", [location?.state?.electronicBillLading]);
      setLinkType('CTE');
    }

    if (location?.state?.invoice) {
      dispatchLinkTypeData("SET_OPEN_PROTOCOL_CTE_INVOICE", [location?.state?.invoice]);
      setLinkType('CTE_INVOICE');
    }

    conditionEnableProduct();
  }, []);

  useEffect(() => {
    getEnterprisesManualProtocol();
    ResetCompany();
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, undefined, {
        and: [
          { identifier: { eq: 'CommunicationChannelType' } },

          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {

          let _optionalButtonTypes: Array<IDomainSetting> = result.data;

          if (!isAutoAttendence)
            _optionalButtonTypes = _optionalButtonTypes.filter(x => x.key !== 'AUTO_ATTENDANCE');

          setOptionalButtonsTypes(_optionalButtonTypes);

        }
      });
  }, []);

  const conditionEnableProduct = () => {
    const categoryValue = location?.state?.ticketCategoryView?.value;
    const isProductListEmpty = !location?.state?.products || location?.state?.products.length === 0;

    const conditionEnable = isProductListEmpty &&
      legalPerson === ILegalPersonType.NATURAL_PERSON &&
      categoryValue === EnumCategories.RECLAMACAO_TECNICA;

    if (conditionEnable) setLinkType('PRODUCT');
  };

  function setRequired() {
    // GET INDEX CATEGORIES
    const indexLogistica = allCategories.find(x => x.label === ITypesRequired.LOGISTICA)?.value;
    const indexFinanceiro = allCategories.find(x => x.label === ITypesRequired.FINANCEIRO)?.value;
    const indexDevolucao = allCategories.find(x => x.label === ITypesRequired.DEVOLUCAO)?.value;
    const indexReclamacao = allCategories.find(x => x.label === ITypesRequired.RECLAMACAO_TECNICA)?.value;
    const indexSolicitacaoInf = allCategories.find(x => x.label === ITypesRequired.SOLICITACAO_DE_INFORMACAO_SERVICO)?.value;

    // GET INDEX REASONS
    const indexCadastroCliente = allReasons.find(x => x.label === ITypesRequired.CADASTRO_FICHA_CLIENTE)?.value;
    const indexCompensacaoNotaCredito = allReasons.find(x => x.label === ITypesRequired.COMPENSACAO_NOTA_CREDITO)?.value;
    const indexCartaTroca = allReasons.find(x => x.label === ITypesRequired.CARTA_TROCA)?.value;

    if (
      protocolCategory === indexLogistica &&
      ticketReason !== indexCadastroCliente

    ) {
      return true;
    }

    if (protocolCategory === indexFinanceiro && ticketReason === indexCompensacaoNotaCredito) {
      return true;
    }

    if (protocolCategory === indexDevolucao || protocolCategory === indexReclamacao) {
      return true;
    }

    if (protocolCategory === indexSolicitacaoInf && ticketReason === indexCartaTroca) {
      return true;
    }

    return false;
  }

  function setRequiredLinkProtocol() {
    if (selectedLinkProtocol && !location?.state?.linkProtocolNumber) {
      return true;
    }

    return false;
  }

  function setRequiredFileReturnProtocol() {
    const typeReturnProtocol = new EqualTo('1');
    //AVARIA DE MERCADORIA E VALIDADE
    const reasonsRequiredFile = new In(['4', '11']);
    //DIAGNÓSTICA CREMER, HEALTHLOG, MAFRA HOSPITALAR - DISTRIBUIÇÃO, TECNOCOLD
    const companiesRequiredFileReturnProtocol = new In(
      ['5e861fe683e503df2a08ccad',
        '5eb4547fdb644465a0429217',
        '5e862b5983e503df2a0989b5',
        '5f09616c2acbd816f4696064'
      ]
    );

    return typeReturnProtocol.isSatisfiedBy(protocolCategory) &&
      reasonsRequiredFile.isSatisfiedBy(ticketReason) &&
      companiesRequiredFileReturnProtocol.isSatisfiedBy(brands);
  }

  function setRequiredFileReclamationProtocol() {
    const typeReclamationProtocol = new EqualTo('7');
    const companiesRequiredFileReclamationProtocol = new In(
      ['5e861fe683e503df2a08ccad',
        '5eb4547fdb644465a0429217',
        '5e862b5983e503df2a0989b5',
        '5e86205683e503df2a08d429'
      ]
    ); // Diagnostica Cremer - Healthlog - Mafra hospitalar - Tecnocold

    return typeReclamationProtocol.isSatisfiedBy(protocolCategory) &&
      companiesRequiredFileReclamationProtocol.isSatisfiedBy(brands);
  }

  useEffect(() => {
    if (files.length !== 0) {
      setFilesRequiredReturn('true');
    } else {
      setFilesRequiredReturn('');
    }
  }, [files]);

  useEffect(() => {
    if (technicalComplaintFile) {
      setFilesRequiredReclamation('true');
    } else {
      setFilesRequiredReclamation('');
    }
  }, [technicalComplaintFile]);

  useEffect(() => {
    const brandsUser = user?.brands;
    const manualForm = brandsUser?.findIndex(x => x.identifier?.toUpperCase() === 'FLEXICOTTON');

    if (manualForm !== -1 && brandsUser && brandsUser.length === 1) {
      setLegalPerson(ILegalPersonType.MANUAL_CUSTOMER_CONSUMER);
    }
  }, [user]);

  const getQtdEnterpriseManualProtocol = useMemo(() => {
    const brandsUser = user?.brands;

    if (brandsUser && brandsUser?.length > 0 && enterprisesManualProtocol.length > 0) {
      const indexManualForm = brandsUser.map(
        x => enterprisesManualProtocol.findIndex(y =>
          x.identifier?.toUpperCase() === y.toUpperCase()));

      const qtdEnterpriseManualForm: number[] = [];
      indexManualForm.forEach(x => {
        if (x !== -1) qtdEnterpriseManualForm.push(x);
      });

      return qtdEnterpriseManualForm.length;
    }

    return 0;
  }, [user, legalPerson, enterprisesManualProtocol]);

  useEffect(() => {
    const brandsUser: number = user?.brands?.length ?? 0;

    if (brandsUser > 0 && brandsUser <= getQtdEnterpriseManualProtocol) {
      setLegalPerson(ILegalPersonType.MANUAL_CUSTOMER_CONSUMER);
    }
  }, [getQtdEnterpriseManualProtocol]);

  const radioFormIntegrations = useCallback(() => {
    const brandsUser = user?.brands;

    if (enterprisesManualProtocol?.length > 0) {
      if ((brandsUser && brandsUser.length > 1 &&
        getQtdEnterpriseManualProtocol < brandsUser.length) ||
        (brandsUser && brandsUser.length === 1 &&
          getQtdEnterpriseManualProtocol === 0)) {
        return (
          <>
            <Row className="w-md-100 mt-md-2" style={{ width: 200 }}>
              <RadioInput
                id="JURIDICAL_PERSON"
                onClick={() =>
                  setLegalPerson(ILegalPersonType.JURIDICAL_PERSON)
                }
                checked={
                  legalPerson === ILegalPersonType.JURIDICAL_PERSON
                }
                classname={disableCustomer && 'cursor-not-allowed'}
                disabled={disableCustomer}
              >
                <Text className="color-blue-gray">Cliente Direto</Text>
              </RadioInput>
            </Row>

            <Row className="w-md-100 mt-md-2" style={{ width: 200 }}>
              <RadioInput
                id="FISICAL_PERSON"
                onClick={() =>
                  setLegalPerson(ILegalPersonType.NATURAL_PERSON)
                }
                checked={
                  legalPerson === ILegalPersonType.NATURAL_PERSON
                }
                classname={disableCustomer && 'cursor-not-allowed'}
                disabled={disableCustomer}
              >
                <Text className="color-blue-gray">Consumidor Final</Text>
              </RadioInput>
            </Row>
          </>
        );
      }
    }

    return false;
  }, [user, legalPerson, enterprisesManualProtocol, getQtdEnterpriseManualProtocol]);

  const radioFormManual = useCallback(() => {
    if (enterprisesManualProtocol?.length > 0) {
      if (getQtdEnterpriseManualProtocol > 0) {
        return (
          <Row className="w-md-100 mt-md-2" style={{ width: 200 }}>
            <RadioInput
              id="MANUAL_CUSTOMER_CONSUMER"
              onClick={() =>
                setLegalPerson(ILegalPersonType.MANUAL_CUSTOMER_CONSUMER)
              }
              checked={
                legalPerson === ILegalPersonType.MANUAL_CUSTOMER_CONSUMER
              }
              classname={disableCustomer && 'cursor-not-allowed'}
              disabled={disableCustomer}
            >
              <Text className="color-blue-gray">Preenchimento Manual</Text>
            </RadioInput>
          </Row>
        );
      }
    }

    return false;
  }, [user, legalPerson, enterprisesManualProtocol, getQtdEnterpriseManualProtocol]);

  const setRequiredAttachment = () => {
    if (protocolCategory === '1') {
      return setRequiredFileReturnProtocol();
    } else if (protocolCategory === '7') {
      return setRequiredFileReclamationProtocol();
    } else if (protocolCategory === '6' && ticketReason === '19') {
      return true;
    }

    return false;
  };

  const nameValidator = () => {
    if (disableProduct)
      return "Pedido";

    return disableOrder ? "Produto" : "Pedido ou Produto";
  };

  const _storageFileService = new StorageFileService();

  const classificationTicketSourceFile = async () => {
    await Promise.all(files.map(async (file, _) => {
      const isFromNewSource = order?.orderAttachments?.some((a: IOrderAttachments) => a.uri == file.uri && a.isFromNewSource) ?? false;
      if (isFromNewSource) {
        const urlFile = (await _storageFileService.getUrlWithSasToken(file.uri, FileOriginType.PROTHEUS)).data;
        return (
          <FloatingLabel
            key={_}
            label={file.name}
            index={_}
            onRemove={removeFile}
            uri={urlFile?.data}
          />
        );
      }

      return (
        <FloatingLabel
          key={_}
          label={file.name}
          index={_}
          onRemove={removeFile}
          uri={file.uri}
        />
      );
    })).then((result) => {
      setFilesMap(result);
    });
  }

useEffect(() => {
  classificationTicketSourceFile();
}, [files, order?.orderAttachments]);

  const titleProtocol = isEditing ? `Edição de Protocolo - ${protocolNumber}` :
    'Abertura de Protocolo';

  const textProtocolSuccess = `O protocolo ${protocolNumberCreated || protocolNumber} foi${' '}
    ${isEditing ? 'alterado' : 'registrado'} com sucesso`;

  return (
    <>
      <Text className="color-primary font-bold mb-2" size={24}>
        {titleProtocol}
      </Text>

      <Visibility visible={success}>
        <Column
          className="bg-white w-100 flex-1 h-100 align-center justify-center"
          style={{
            borderRadius: '10px 10px 0px 0px',
            height: 'calc(100vh - 100px)',
          }}
        >
          <img src={IconSuccess} alt="sucess" />
          <Text className="mt-5 color-blue-gray">
            {textProtocolSuccess}
          </Text>
          <Link className="mt-5" to="/dashboard">
            <Button className="outline" width="auto">
              <Text className="color-primary font-bold px-2">
                Ir para a Dashboard
              </Text>
            </Button>
          </Link>
        </Column>
      </Visibility>

      <CustomerDataContextProvider>
        <Visibility visible={!success}>
          <Form
            id="open_protocol_form"
            onValidate={(data: any) => onCheckSubmit(data)}
            customFunction={(result: boolean) => setFail(result)}
            disableEnterSubmit={true}
          >
            <Column
              className="bg-white flex-1 p-3"
              style={{ borderRadius: '10px 10px 0px 0px' }}
            >
              <SwitchComponent roles={['administrator', 'member']} type={['employee']} >
                <Column>
                  <Text className="mb-2 color-blue-gray" size={12}>
                    Tipo de Usuário
                  </Text>
                  <Row className="flex-md-column">
                    {radioFormIntegrations()}
                    {radioFormManual()}
                  </Row>
                </Column>
              </SwitchComponent>

              {legalPerson !== ILegalPersonType.MANUAL_CUSTOMER_CONSUMER &&
                <>
                  <SwitchComponent
                    roles={['administrator', 'member']}
                    type={['employee']}
                  >
                    <CustomerData
                      company={company}
                      legalPerson={legalPerson}
                      disableCustomer={disableCustomer}
                      customerId={customerId}
                      setCustomerId={setCustomerId}
                    />

                    <Column className="my-5">
                      <Text className="mb-2 color-blue-gray" size={12}>
                        Canal de Contato
                      </Text>
                      <Validator
                        value={communicationChannel}
                        name="Canal de contato"
                        required
                      >
                        <Row className="flex-md-column">
                          {
                            optionalButtonsTypes.map((el, index) => (
                              <Row
                                key={index}
                                className="w-md-100 mt-md-2 mr-4"
                              >
                                <RadioInput
                                  id={el.key}
                                  onClick={() => setCommunicationChannel(el.key)}
                                  checked={communicationChannel === el.key}
                                >
                                  <Text className="color-blue-gray">{el.value}</Text>
                                </RadioInput>
                              </Row>
                            ))
                          }
                        </Row>
                      </Validator>
                    </Column>
                  </SwitchComponent>

                  <ProtocolCategory
                    protocolCategory={protocolCategory}
                    setProtocolCategory={setProtocolCategory}
                    ticketReason={ticketReason}
                    setTicketReason={setTicketReason}
                    setDisableProduct={setDisableProduct}
                    setDisableOrder={setDisableOrder}
                    protocolClassification={protocolClassification}
                    setProtocolClassification={setProtocolClassification}
                    legalPerson={legalPerson}
                  />

                  <ContactData
                    protocolCategory={protocolCategory}
                    office={office}
                    setOffice={setOffice}
                  />

                  {
                    protocolCategory === EnumCategories.RECLAMACAO_TECNICA &&
                    legalPerson === ILegalPersonType.JURIDICAL_PERSON && (
                      <OccurrenceType
                        protocolCategory={protocolCategory}
                        othersOccurrence={othersOccurrence}
                        setOthersOccurrence={setOthersOccurrence}
                        occurrenceType={occurrenceType}
                        setOccurrenceType={setOccurrenceType}
                        isEditing={isEditing}
                      />
                    )
                  }

                  <DropAddress
                    protocolCategory={protocolCategory}
                    returnType={returnType}
                    setReturnType={setReturnType}
                    dropAddressSameInvoice={dropAddressSameInvoice}
                    setDropAddressSameInvoice={setDropAddressSameInvoice}
                    dropAddress={dropAddress}
                    setDropAddress={setDropAddress}
                  />

                  <Validator
                    value={linkType}
                    name={nameValidator()}
                    required={setRequired()}
                  >
                    <LinkType
                      products={products}
                      dispatchLinkTypeData={dispatchLinkTypeData}
                      order={order}
                      linkType={linkType}
                      setLinkType={setLinkType}
                      disableProduct={disableProduct}
                      disableOrder={disableOrder}
                      protocolCategory={protocolCategory}
                      returnType={returnType}
                      brands={brands}
                      isEditing={isEditing}
                      ticketReason={ticketReason}
                      legalPerson={legalPerson}
                    />
                  </Validator>

                  <Row className='flex-1 flex-md-column'>
                    {
                      (protocolCategory === EnumCategories.RECLAMACAO_TECNICA || protocolCategory === 'RECLAMAÇÃO TÉCNICA') &&
                      legalPerson === ILegalPersonType.NATURAL_PERSON &&
                      (
                        <LotProduct lot={lot} setLot={setLot} products={products} isEditing={isEditing} />
                      )
                    }

                    {
                      (protocolCategory === EnumCategories.RECLAMACAO_TECNICA || protocolCategory === 'RECLAMAÇÃO TÉCNICA') &&
                      brands === '6179b025389e8d5c5f72fbb7' &&
                      (
                        <RIProduct productRI={productRI} setProductRI={setProductRI} legalPerson={legalPerson} />
                      )
                    }
                  </Row>

                  <NfDevolution
                    manualDevolutionInvoiceNumber={manualDevolutionInvoiceNumber}
                    setManualDevolutionInvoiceNumber={setManualDevolutionInvoiceNumber}
                    protocolCategory={protocolCategory}
                    ticketReason={ticketReason}
                  />

                  <Row className='flex-1 flex-md-column'>
                    <Brand
                      protocolCategory={protocolCategory}
                      brands={brands}
                      setBrands={setBrands}
                    />

                    <SwitchComponent
                      roles={['administrator', 'member']}
                      type={['employee']}
                    >
                      <BU
                        buSelected={buSelected}
                        setBuSelected={setBuSelected}
                      />
                    </SwitchComponent>
                  </Row>

                  <Visibility visible={protocolCategory !== EnumCategories.RECLAMACAO_TECNICA}>
                    <Description {...location} isEditing={isEditing} />
                  </Visibility>

                  <Visibility visible={protocolCategory === EnumCategories.COMERCIAL && ticketReason === '19'}>
                    <DiscountValue
                      discountValue={discountValue}
                      setDiscountValue={setDiscountValue}
                      isEditing={isEditing}
                    />
                  </Visibility>

                  {
                    protocolCategory === EnumCategories.RECLAMACAO_TECNICA &&
                    legalPerson === ILegalPersonType.JURIDICAL_PERSON &&
                    (
                      <RemainingBalance
                        protocolCategory={protocolCategory}
                        remainingBalance={remainingBalance}
                        setRemainingBalance={setRemainingBalance}
                        qtdRemainingBalance={qtdRemainingBalance}
                        setQtdRemainingBalance={setQtdRemainingBalance}
                        isEditing={isEditing}
                      />
                    )
                  }

                  {
                    protocolCategory === '7' &&
                    (
                      <TechnicalDescription
                        protocolCategory={protocolCategory}
                        technicalDescription={technicalDescription}
                        setTechnicalDescription={setTechnicalDescription}
                        isEditing={isEditing}
                      />
                    )
                  }

                  {
                    protocolCategory === '7' &&
                    legalPerson === ILegalPersonType.JURIDICAL_PERSON &&
                    (
                      <>
                        <OccurrenceDate
                          protocolCategory={protocolCategory}
                          occurrenceDate={occurrenceDate}
                          setOccurrenceDate={setOccurrenceDate}
                          isEditing={isEditing}
                        />

                        <Row className="align-center justify-between">
                          <ProductSampleType
                            protocolCategory={protocolCategory}
                            uncontaminatedProductSampleType={uncontaminatedProductSampleType}
                            setUncontaminatedProductSampleType={setUncontaminatedProductSampleType}
                            isEditing={isEditing}
                          />
                          <SampleType
                            protocolCategory={protocolCategory}
                            sampleType={sampleType}
                            setSampleType={setSampleType}
                            isEditing={isEditing}
                          />
                        </Row>
                      </>
                    )
                  }

                  <Validator value={filesRequiredReturn} name="Anexo" required={setRequiredAttachment()}>
                    <Column className="mt-5">
                      <Row className="align-center">
                        <img src={attachmentIcon} alt="" />
                        <Text className="ml-1 color-blue-gray" size={12}>
                          {protocolCategory === '7'
                            ? 'Anexar fotos ou vídeos com as evidências da reclamação'
                            : 'Anexar Arquivos'}
                        </Text>
                        <input
                          onChange={addFile}
                          id="open_protocol_files"
                          className="d-none"
                          type="file"
                          multiple
                          accept={FileTypesAccept.toString()}
                        />
                      </Row>
                      <Row className="mt-1 align-center align-sm-none flex-sm-column  ">
                        <label className="button outline" htmlFor="open_protocol_files" id="select-file">
                          <Text className="px-2 color-primary font-bold">
                            Selecionar Arquivo
                          </Text>
                        </label>
                        <Row className="ml-2 ml-sm-0 my-sm-2 align-center flex-wrap word-break">
                          {filesMap}
                        </Row>
                      </Row>
                    </Column>
                  </Validator>

                  {user?.userType !== 'customer' &&
                    <Validator
                      value={linkProtocol}
                      name="Protocolo vinculado"
                      required={setRequiredLinkProtocol()}
                    >
                      <LinkProtocol
                        setLinkProtocol={setLinkProtocol}
                        setSelectedLinkProtocol={setSelectedLinkProtocol}
                        selectedLinkProtocol={selectedLinkProtocol}
                        protocolNumber={location?.state?.protocolNumber}
                        linkProtocolNumber={location?.state?.linkProtocolNumber}
                        isEditing={isEditing}
                        customerId={customerId}
                      />
                    </Validator>
                  }
                  <SwitchComponent
                    roles={['administrator', 'member']}
                    type={['employee']}
                  >
                    <Column className="mt-5">
                      <Text className="mb-2 color-blue-gray" size={12}>
                        Adicionar Tags
                      </Text>
                      <Row
                        className="mb-2 flex-1 align-center px-2 flex-wrap"
                        style={{ borderRadius: 6, border: '1px solid #a0a0a0' }}
                      >
                        {tags?.map((tag: any, _: number) => (
                          <FloatingLabel
                            key={_}
                            label={tag}
                            index={_}
                            onRemove={removeTag}
                          />
                        ))}
                        <input
                          className="flex-1"
                          placeholder={`Digite a tag e pressione ENTER ou ','`}
                          onKeyDown={(e: any) => addNewTag(e)}
                          type="text"
                          onChange={(e: any) => setTagInput(e.target.value)}
                          value={tagInput}
                          style={{ border: 'none', minWidth: 180 }}
                          id="select-file"
                        />
                      </Row>
                    </Column>
                  </SwitchComponent>

                  {fail && (
                    <Text size={14} className="color-danger animated fadeIn mt-3">
                      Favor revisar o preenchimento do formulário
                    </Text>
                  )}

                  {errorMessage && (
                    <Text size={14} className="color-danger animated fadeIn mt-3">
                      {errorMessage}
                    </Text>
                  )}

                  <Row className="mt-3 bg-gray flex-1 justify-center align-center">
                    <Row className="py-4">
                      <Button
                        isRequesting={loading}
                        form="open_protocol_form"
                        className="bg-primary"
                        width="auto"
                        id="register-protocol"
                      >
                        <Text className="color-white font-bold px-3">
                          {isEditing ? 'Salvar Protocolo' : 'Registrar Protocolo'}
                        </Text>
                      </Button>
                    </Row>
                  </Row>
                </>
              }
            </Column>
          </Form>
          {user?.userType === 'employee' && legalPerson === ILegalPersonType.MANUAL_CUSTOMER_CONSUMER &&
            <SimpleForm
              stateLocation={location?.state}
              setSuccess={setSuccess}
              setProtocolNumberCreated={setProtocolNumberCreated}
              ResetCompany={ResetCompany}
            />
          }
        </Visibility>
      </CustomerDataContextProvider>
    </>
  );
};

const OpenProtocol: React.FC = (props: any) => {
  return (
    <OpenProtocolContextProvider>
      <OpenProtocolMain {...props} />
    </OpenProtocolContextProvider>
  );
};

export default OpenProtocol;
